'use client'

import { recordAdClick, recordAdImpression } from '@/actions/ads-tracking'
import { hasAdvertisingCookieConsent } from '@/components/cookie-consent-banner'
import { getSessionId } from '@/components/utils/session-tracking'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

export default function AdBanner({ ad, placement }) {
  const [hasTrackedImpression, setHasTrackedImpression] = useState(false)
  const bannerRef = useRef(null)
  const pathname = usePathname()

  // Track viewable impression using Intersection Observer
  useEffect(() => {
    if (!ad || hasTrackedImpression) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedImpression) {
          // Wait 1 second to ensure it's truly viewed
          setTimeout(() => {
            if (entry.isIntersecting && !hasTrackedImpression) {
              const hasConsent = hasAdvertisingCookieConsent()
              const sessionId = hasConsent ? getSessionId() : null

              recordAdImpression({
                adId: ad.id,
                page: pathname,
                placement,
                sessionId,
              })
                .then(() => {
                  // Impression recorded successfully
                })
                .catch((error) => {
                  console.error('Failed to record ad impression:', error)
                })
              setHasTrackedImpression(true)
            }
          }, 1000)
        }
      },
      {
        threshold: 0.5, // 50% of the ad must be visible
        rootMargin: '0px',
      },
    )

    if (bannerRef.current) {
      observer.observe(bannerRef.current)
    }

    return () => {
      if (bannerRef.current) {
        observer.unobserve(bannerRef.current)
      }
    }
  }, [ad, hasTrackedImpression, placement, pathname])

  const handleBannerClick = () => {
    // Navigate immediately for better UX
    if (ad.targetUrl) {
      window.open(ad.targetUrl, '_blank', 'noopener,noreferrer')
    }

    const consent = hasAdvertisingCookieConsent()
    if (!consent) {
      return
    }

    // Track the click in the background (fire-and-forget)
    const sessionId = getSessionId()

    recordAdClick({
      adId: ad.id,
      fromPage: pathname,
      placement,
      sessionId,
    }).catch((error) => {
      // Log error but don't interrupt user experience
      console.error('Failed to record ad click:', error)
    })
  }

  if (!ad || !ad.bannerImgUrl) return null

  return (
    <div className="hidden w-full desktop-xs:block">
      <div className="mx-auto w-fit">
        <button
          onClick={handleBannerClick}
          ref={bannerRef}
          className="group relative block overflow-hidden rounded-sm transition-all duration-200 hover:shadow-lg"
        >
          <div className="relative h-[250px] w-[970px]">
            <Image
              src={ad.bannerImgUrl}
              alt={ad.title || 'Advertisement'}
              fill
              className="object-cover transition-transform duration-200 group-hover:scale-105"
              sizes="970px"
              priority={false}
            />

            {/* Overlay with subtle hover effect */}
            <div className="absolute inset-0 bg-black/0 transition-colors duration-200 group-hover:bg-black/5" />

            {/* Optional overlay content (title, CTA) - uncomment if needed */}
            {/*
            <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/60 to-transparent p-4">
              <h3 className="text-white font-semibold text-lg">{ad.title}</h3>
              {ad.ctaText && (
                <span className="inline-block mt-2 px-4 py-2 bg-white text-black font-medium rounded">
                  {ad.ctaText}
                </span>
              )}
            </div>
            */}
          </div>
        </button>
      </div>
    </div>
  )
}
